/**
 * BrandProvider — Feb 2026.
 *
 * Resolves the active brand once per render tree and exposes it to
 * consumers via useBrand(). Writes CSS variables (--brand-accent,
 * --brand-accent-dim, --brand-accent-soft) to <html> so any component
 * can pick them up via `style={{ color: 'var(--brand-accent)' }}` or
 * arbitrary Tailwind (`text-[color:var(--brand-accent)]`).
 *
 * Persistence: a URL param (?brand=quad) is written to localStorage so
 * subsequent client-side nav preserves the theme without keeping the
 * param on every URL.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { BRANDS, resolveBrandId } from "@/lib/brands";

const BRAND_STORAGE_KEY = "brand-override";

const BrandContext = createContext({
  brand: BRANDS.default,
  clearOverride: () => {},
});

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  const [storedOverride, setStoredOverride] = useState(() => {
    try {
      return window.localStorage.getItem(BRAND_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });

  // Sniff ?brand=… on every navigation. If present + known, persist so
  // subsequent nav retains the theme without the param on every URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlBrand = (params.get("brand") || "").trim().toLowerCase();
    if (urlBrand && BRANDS[urlBrand]) {
      try {
        window.localStorage.setItem(BRAND_STORAGE_KEY, urlBrand);
      } catch {
        /* private mode — non-fatal, override still active for this tab */
      }
      setStoredOverride(urlBrand);
    }
  }, [location.search]);

  const brandId = useMemo(
    () =>
      resolveBrandId({
        urlParam: new URLSearchParams(location.search).get("brand"),
        storedOverride,
        email: user?.email,
      }),
    [location.search, storedOverride, user?.email],
  );

  const brand = BRANDS[brandId] || BRANDS.default;

  // Write CSS variables + a data-brand attribute so descendant styles
  // can react (Tailwind arbitrary values, or targeted CSS selectors).
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-accent", brand.accent);
    root.style.setProperty("--brand-accent-dim", brand.accentDim);
    root.style.setProperty("--brand-accent-soft", brand.accentSoft);
    root.setAttribute("data-brand", brand.id);
    // Light-mode brands (e.g. Quad) flip the base palette via a
    // scoped [data-brand=".."] block in index.css. We also expose
    // the full palette as CSS vars so inline styles / arbitrary
    // Tailwind values can pick them up.
    if (brand.mode === "light") {
      root.setAttribute("data-mode", "light");
    } else {
      root.removeAttribute("data-mode");
    }
    const p = brand.palette || {};
    root.style.setProperty("--brand-page-bg", p.pageBg || "");
    root.style.setProperty("--brand-surface", p.surface || "");
    root.style.setProperty("--brand-surface-alt", p.surfaceAlt || "");
    root.style.setProperty("--brand-sidebar-bg", p.sidebarBg || "");
    root.style.setProperty("--brand-sidebar-fg", p.sidebarFg || "");
    root.style.setProperty("--brand-sidebar-muted", p.sidebarMuted || "");
    root.style.setProperty("--brand-sidebar-active", p.sidebarActive || "");
    root.style.setProperty("--brand-border", p.border || "");
    root.style.setProperty("--brand-text", p.text || "");
    root.style.setProperty("--brand-text-muted", p.textMuted || "");
    // Browser tab title stays fresh so favicon/tab reads correctly.
    if (brand.portalTitle) document.title = brand.portalTitle;
  }, [brand]);

  const clearOverride = () => {
    try {
      window.localStorage.removeItem(BRAND_STORAGE_KEY);
    } catch {
      /* no-op */
    }
    setStoredOverride("");
  };

  const value = useMemo(() => ({ brand, clearOverride }), [brand]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  return useContext(BrandContext);
}

/** Renders the active brand's logo. Prefers `logoUrl` when set,
 *  falls back to a text wordmark. Copyright-safe by default — a
 *  wordmark is not a reproduction of any real trademarked logo.
 */
export function BrandLogo({ className = "", size = "md" }) {
  const { brand } = useBrand();
  // Base heights in px (h-8 / h-10 / h-14). Per-brand `logoScale` lets
  // tall/square marks fill more of the sidebar chrome without touching
  // brands whose logo is already wide enough (e.g. Quad).
  const basePx = size === "sm" ? 32 : size === "lg" ? 56 : 40;
  const scale = brand.logoScale || 1;
  const heightPx = Math.round(basePx * scale);

  if (brand.logoUrl) {
    return (
      <img
        src={brand.logoUrl}
        alt={brand.portalTitle}
        data-testid="brand-logo"
        style={{ height: `${heightPx}px` }}
        className={`w-auto object-contain select-none ${className}`}
        draggable="false"
      />
    );
  }

  if (brand.wordmark) {
    const textCls =
      size === "sm"
        ? "text-lg tracking-[0.35em]"
        : size === "lg"
          ? "text-3xl tracking-[0.4em]"
          : "text-2xl tracking-[0.35em]";
    const subCls = size === "sm" ? "text-[8px]" : "text-[9px]";
    return (
      <div
        data-testid="brand-wordmark"
        className={`flex flex-col leading-none select-none ${className}`}
      >
        <span
          className={`${textCls} font-light uppercase`}
          style={{ color: "var(--brand-accent)" }}
        >
          {brand.wordmark.text}
        </span>
        {brand.wordmark.subtitle ? (
          <span
            className={`${subCls} font-mono uppercase tracking-[0.28em] text-zinc-500 mt-1.5`}
          >
            {brand.wordmark.subtitle}
          </span>
        ) : null}
      </div>
    );
  }

  // Should not happen — every brand has either a logoUrl or a wordmark.
  return (
    <span
      data-testid="brand-wordmark"
      className={`font-heading font-light text-zinc-100 ${className}`}
    >
      {brand.portalTitle}
    </span>
  );
}
